/**
 * App-lock gate — biometric re-entry lock.
 *
 * When the user turns on "Require Face ID / Touch ID to open the app"
 * (persistentSettings key {@link APP_LOCK_SETTING_KEY}), this gate re-locks the
 * app after it returns from the background beyond {@link RELOCK_AFTER_MS} and
 * blocks the UI behind a full-screen overlay until the local biometric prompt
 * succeeds. It is deliberately a plain absolutely-positioned View (NOT a RN
 * `Modal`): OS dialogs over an RN Modal have caused frozen backdrops on iOS in
 * this app, so the overlay stays part of the normal view tree.
 *
 * Cold-start entry is already gated by StartScreen; this only covers the
 * background → foreground transition. Purely local — no server round-trip.
 */

import React from 'react';
import {AppState, Image, Platform, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {getAccessToken} from '../helpers/storage';
import {getSetting} from '../helpers/persistentSettings';
import {authenticateBiometric, getBiometricKind, isBiometricAvailable} from '../helpers/biometricAuth';
import {useTheme} from '../helpers/ThemeContext';
import LogoImage from '../../assets/logo-small.png';

export const APP_LOCK_SETTING_KEY = 'appLockBiometric';
const RELOCK_AFTER_MS = 20 * 1000;

/** @returns {React.ReactElement|null} the lock overlay while locked, else null. */
export default function AppLockGate() {
    const {t} = useTranslation();
    const {theme, isDark} = useTheme();
    const [locked, setLocked] = React.useState(false);
    const [kind, setKind] = React.useState('biometric');
    const backgroundedAt = React.useRef(0);
    const promptingRef = React.useRef(false);

    /** @returns {Promise<boolean>} whether the app should re-lock on this foreground. */
    const shouldLock = React.useCallback(async () => {
        try {
            if (Platform.OS === 'web') {
                return false;
            }
            const enabled = (await getSetting(APP_LOCK_SETTING_KEY, 'false')) === 'true';

            if (!enabled) {
                return false;
            }
            const token = await getAccessToken();

            if (!token) {
                return false;
            }

            return await isBiometricAvailable();
        } catch (_e) {
            return false;
        }
    }, []);

    const promptUnlock = React.useCallback(async () => {
        if (promptingRef.current) {
            return;
        }
        promptingRef.current = true;
        try {
            const ok = await authenticateBiometric(
                t('app_lock_prompt', {defaultValue: 'Unlock to continue'})
            );

            if (ok) {
                setLocked(false);
            }
        } finally {
            promptingRef.current = false;
        }
    }, [t]);

    React.useEffect(() => {
        const sub = AppState.addEventListener('change', async (next) => {
            if (next === 'background' || next === 'inactive') {
                if (!backgroundedAt.current) {
                    backgroundedAt.current = Date.now();
                }

                return;
            }
            if (next === 'active') {
                const away = backgroundedAt.current ? Date.now() - backgroundedAt.current : 0;

                backgroundedAt.current = 0;
                if (away >= RELOCK_AFTER_MS && await shouldLock()) {
                    setKind(await getBiometricKind());
                    setLocked(true);
                }
            }
        });

        return () => sub.remove();
    }, [shouldLock]);

    // Auto-present the OS biometric sheet as soon as the overlay appears.
    React.useEffect(() => {
        if (locked) {
            promptUnlock();
        }
    }, [locked, promptUnlock]);

    if (!locked) {
        return null;
    }

    const unlockLabel = kind === 'faceId'
        ? t('app_lock_unlock_faceid', {defaultValue: 'Unlock with Face ID'})
        : kind === 'fingerprint'
            ? t('app_lock_unlock_touchid', {defaultValue: 'Unlock with Touch ID'})
            : t('app_lock_unlock', {defaultValue: 'Unlock'});

    return (
        <View style={[styles.overlay, {backgroundColor: isDark ? '#0B1220' : '#0F172A'}]}>
            <Image source={LogoImage} style={styles.logo} />
            <Text style={styles.title}>{t('app_lock_title', {defaultValue: 'App locked'})}</Text>
            <Text style={styles.subtitle}>
                {t('app_lock_subtitle', {defaultValue: 'Confirm your identity to continue.'})}
            </Text>
            <TouchableOpacity
                style={[styles.unlockBtn, {backgroundColor: theme.colors.primary}]}
                activeOpacity={0.9}
                onPress={promptUnlock}
            >
                <Text style={styles.unlockBtnText}>{unlockLabel}</Text>
            </TouchableOpacity>
        </View>
    );
}

const styles = StyleSheet.create({
    overlay: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 9999,
        elevation: 9999,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
    },
    logo: {width: 96, height: 96, marginBottom: 28, resizeMode: 'contain'},
    title: {fontSize: 22, fontWeight: '800', color: '#fff', textAlign: 'center'},
    subtitle: {marginTop: 8, fontSize: 14, lineHeight: 20, color: 'rgba(255,255,255,0.75)', textAlign: 'center'},
    unlockBtn: {
        marginTop: 30,
        paddingVertical: 15,
        paddingHorizontal: 34,
        borderRadius: 14,
    },
    unlockBtnText: {color: '#fff', fontSize: 16, fontWeight: '800'},
});
